import { demoFunc } from './dees-harness-reasoning.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type { IHarnessReasoningPart } from '../interfaces.js';
import { harnessFormatDuration } from '../harness.helpers.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-reasoning': DeesHarnessReasoning;
  }
}

/**
 * Reasoning ("thinking") disclosure for one reasoning part. Auto-opens while
 * the part is streaming (no endedAt) unless the user has interacted; the
 * user's expand/collapse choice wins afterwards and survives re-renders
 * because it lives on this element, not in the parent's template.
 */
@customElement('dees-harness-reasoning')
export class DeesHarnessReasoning extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor reasoningPart: IHarnessReasoningPart = { id: '', text: '' };

  @state()
  accessor expanded: boolean | undefined = undefined;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
      }

      .reasoning {
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-bg-tertiary);
        overflow: hidden;
        animation: harness-reasoning-enter var(--dees-transition-default) var(--dees-ease-out);
      }

      @keyframes harness-reasoning-enter {
        from {
          opacity: 0;
          transform: translateY(-3px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Expand/collapse animates through the 0fr→1fr grid trick so the body
         stays in the DOM and both directions transition smoothly. */
      .bodyWrap {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows var(--dees-transition-default) var(--dees-ease-standard);
      }

      .bodyWrap.open {
        grid-template-rows: 1fr;
      }

      .bodyWrap > .bodyClip {
        overflow: hidden;
        min-height: 0;
      }

      @media (prefers-reduced-motion: reduce) {
        .reasoning {
          animation: none;
        }
        .bodyWrap,
        .chevron {
          transition: none;
        }
      }

      .summary {
        width: 100%;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) var(--dees-spacing-md);
        border: 0;
        background: transparent;
        color: var(--dees-color-text-secondary);
        cursor: pointer;
        font-family: inherit;
        font-size: var(--dees-font-caption1-size, 11px);
        font-weight: 600;
        text-align: left;
      }

      .summary:hover {
        color: var(--dees-color-text-primary);
      }

      .chevron {
        display: grid;
        place-items: center;
        transition: transform var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .chevron.open {
        transform: rotate(90deg);
      }

      .duration {
        margin-left: auto;
        color: var(--dees-color-text-muted);
        font-weight: 400;
      }

      .thinkingDots::after {
        content: '';
        display: inline-block;
        width: 1.2em;
        text-align: left;
        animation: harness-reasoning-dots 1.2s steps(4, end) infinite;
      }

      @keyframes harness-reasoning-dots {
        0% { content: ''; }
        25% { content: '.'; }
        50% { content: '..'; }
        75%, 100% { content: '...'; }
      }

      .body {
        margin: 0;
        padding: 0 var(--dees-spacing-md) var(--dees-spacing-sm);
        color: var(--dees-color-text-secondary);
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        font-size: var(--dees-font-caption1-size, 11px);
        line-height: 1.5;
      }
    `,
  ];

  public get isStreaming(): boolean {
    return Boolean(this.reasoningPart.startedAt) && !this.reasoningPart.endedAt;
  }

  public get isOpen(): boolean {
    return this.expanded ?? this.isStreaming;
  }

  public render(): TemplateResult {
    const duration = harnessFormatDuration(this.reasoningPart.startedAt, this.reasoningPart.endedAt);
    return html`
      <div class="reasoning">
        <button
          class="summary"
          type="button"
          aria-expanded=${this.isOpen ? 'true' : 'false'}
          @click=${() => {
            this.expanded = !this.isOpen;
          }}
        >
          <span class="chevron ${this.isOpen ? 'open' : ''}">
            <dees-icon icon="lucide:ChevronRight" iconSize="12"></dees-icon>
          </span>
          <span class=${this.isStreaming ? 'thinkingDots' : ''}>
            ${this.isStreaming ? 'Thinking' : 'Thought'}
          </span>
          ${duration ? html`<span class="duration">${duration}</span>` : ''}
        </button>
        <div class="bodyWrap ${this.isOpen ? 'open' : ''}" aria-hidden=${this.isOpen ? 'false' : 'true'}>
          <div class="bodyClip">
            <pre class="body">${this.reasoningPart.text || (this.isStreaming ? 'Waiting for reasoning…' : 'No reasoning emitted.')}</pre>
          </div>
        </div>
      </div>
    `;
  }
}
